<template>
  <block-item-card
    :title="$t('Rates.Section.RepricingSpeed.Title')"
    :title-info="$t('Rates.Section.RepricingSpeed.TitleInfo')"
    :tooltip="$t('Rates.Section.RepricingSpeed.Tooltip')"
    class="fill-height"
  >
    <v-row class="ma-0 pa-0">
      <!-- Loans -->
      <v-col
        class="px-4 py-2"
        cols="6"
      >
        <div class="panel">
          <h3 class="panel-title text-center">
            {{ $t('Rates.Section.RepricingSpeed.Loans') }}
          </h3>

          <!-- Current -->
          <div class="gauge-wrap">
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <e-chart
                :option="gaugeOption(loansCurrent, loansCurrentColor)"
                class="gauge"
                style="width: 100%; height: 100%; pointer-events:none;"
                renderer="canvas"
                :autoresize="true"
              />
            </v-lazy>
            <div class="gauge-label">
              <div class="gauge-sub">
                {{ $t('Rates.Section.RepricingSpeed.CurrentSpeed') }}
              </div>
            </div>
          </div>

          <!-- Last Year -->
          <div class="gauge-wrap mt-6">
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <e-chart
                :option="gaugeOption(loansLastYear, loansLastYearColor)"
                class="gauge"
                style="width:100%; height: 100%; pointer-events:none;"
                renderer="canvas"
                :autoresize="true"
              />
            </v-lazy>
            <div class="gauge-label">
              <div class="gauge-sub">
                {{ $t('Rates.Section.RepricingSpeed.LastYearSpeed') }}
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Deposits -->
      <v-col
        class="px-4 py-2"
        cols="6"
      >
        <div class="panel">
          <h3 class="panel-title text-center">
            {{ $t('Rates.Section.RepricingSpeed.Deposits') }}
          </h3>

          <!-- Current -->
          <div class="gauge-wrap">
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <e-chart
                :option="gaugeOption(depositsCurrent, depositsCurrentColor)"
                class="gauge"
                style="width: 100%; height: 100%; pointer-events:none;"
                renderer="canvas"
                :autoresize="true"
              />
            </v-lazy>
            <div class="gauge-label">
              <div class="gauge-sub">
                {{ $t('Rates.Section.RepricingSpeed.CurrentSpeed') }}
              </div>
            </div>
          </div>

          <!-- Last Year -->
          <div class="gauge-wrap mt-6">
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <e-chart
                :option="gaugeOption(depositsLastYear, depositsLastYearColor)"
                class="gauge"
                style="width: 100%; height: 100%; pointer-events:none;"
                renderer="canvas"
                :autoresize="true"
              />
            </v-lazy>
            <div
              class="gauge-label"
            >
              <div class="gauge-sub">
                {{ $t('Rates.Section.RepricingSpeed.LastYearSpeed') }}
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

  <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { use } from 'echarts/core'
import { GaugeChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([GaugeChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

export default {
  name      : 'RepricingSpeed',
  components: { BlockItemCard },
  mixins    : [chartsColorMixin],
  dataStore : { FindBankRegressionData: 'Rates.FindBankRegressionData.Data' },

  computed: {
    repricing () {
      return this.FindBankRegressionData?.bank?.repricingSpeed || {}
    },
    loansCurrent () {
      return Number(this.repricing?.loans?.current ?? 0)
    },
    loansLastYear () {
      return Number(this.repricing?.loans?.lastYear ?? 0)
    },
    depositsCurrent () {
      return Number(this.repricing?.deposits?.current ?? 0)
    },
    depositsLastYear () {
      return Number(this.repricing?.deposits?.lastYear ?? 0)
    },

    // colors
    loansCurrentColor () { return this.themeColorRGBA('synthesis-ui-green-02', this.loansCurrent / 100) },
    loansLastYearColor () { return this.themeColorRGBA('synthesis-ui-green-02', this.loansLastYear / 100) },
    depositsCurrentColor () { return this.themeColorRGBA('synthesis-ui-orange-02', this.depositsCurrent / 100) },
    depositsLastYearColor () { return this.themeColorRGBA('synthesis-ui-orange-02', this.depositsLastYear / 100) },
    trackColor () { return this.themeColorRGBA('synthesis-ui-blue-07') }
  },

  methods: {
    gaugeOption (value = 0, mainColor) {
      const v = Math.max(0, Math.min(100, Number(value)))
      const width = 12

      return {
        animation        : true,
        animationDuration: 800,
        color            : mainColor,
        grid             : { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
        series           : [{
          type      : 'gauge',
          startAngle: 180,
          endAngle  : 0,
          min       : 0,
          max       : 100,
          radius    : '100%',
          center    : ['50%', '70%'],
          progress  : { show: true, roundCap: true, width },
          pointer   : { show: false },
          axisLine  : {
            roundCap : true,
            lineStyle: {
              width,
              color: [
                [v / 100, mainColor],
                [1, this.trackColor]
              ]
            }
          },
          splitLine: { show: false },
          axisTick : { show: false },
          axisLabel: { show: false },
          // detail   : { show: true, formatter: '{value}%' },
          detail   : {
            show          : true,
            valueAnimation: true,
            offsetCenter  : [0, '-12%'],
            formatter     : (val) =>
              `{val|${Math.round(val)}%}`,
            rich: {
              val: {
                fontSize  : 22,
                fontWeight: 700,
                color     : '#041F30',
                lineHeight: 26
              }

            }
          },

          data: [{ value: v }]
        }]
      }
    }
  }
}
  </script>

  <style scoped>
  .panel {
    background-color: var(--v-synthesis-ui-grey-06-base);
    border-radius: 12px;
    padding: 8px;
  }
  .panel-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--v-synthesis-brand-dark-blue-base, #041F30);
    margin-bottom: 6px;
  }

  .gauge-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 6px;
  }
  .gauge-value {
    font-size: 22px;
    font-weight: 700;
    color: #041F30;
    line-height: 1;
  }
  .gauge-sub {
    font-size: 12px;
    color: #63747D;
    margin-top: -20px;
  }
  </style>
